<template>
  <main class="milon-dev">
    <header class="milon-dev__header">
      <span class="milon-dev__badge">STAGING · DEV-BEREICH</span>
      <h1>Milon Testdaten</h1>
      <p>Wir prüfen die verfügbaren Daten für deine neue Trainingsstatistik.</p>
    </header>

    <section class="milon-dev__card" aria-labelledby="milon-source">
      <h2 id="milon-source">Datenquelle</h2>
      <p><strong>Test User · 9153</strong><br />Studio 1040 · Studiogruppe 1281</p>
      <p class="milon-dev__secondary">Dieser Bereich liest ausschliesslich das festgelegte Milon-Testkonto. Er erstellt keine Trainings und verknüpft keine Benutzerkonten.</p>
      <div class="milon-dev__fields">
        <label>Von<input v-model="from" type="date" :disabled="loading" /></label>
        <label>Bis einschliesslich<input v-model="to" type="date" :disabled="loading" /></label>
      </div>
      <p class="milon-dev__secondary">Maximal 31 Tage, Datumsgrenzen in UTC. Der Zeitraum gilt für Kraft und Cardio. Der zusätzliche Abruf «Nur Benutzer-ID» sendet keine Filter und lädt die erste Antwortseite mit Milon-Standardwerten; die übrigen Bereiche liefern den aktuellen Stand.</p>
      <label class="milon-dev__check"><input v-model="raw" type="checkbox" :disabled="loading" /> Trainings-Rohdaten zur Analyse einschliessen</label>
      <p v-if="rangeError" class="milon-dev__error" role="alert">{{ rangeError }}</p>
      <div class="milon-dev__actions">
        <button class="milon-dev__primary" :disabled="loading || !!rangeError" @click="loadData">{{ loading ? 'Daten werden geladen …' : 'Testdaten laden' }}</button>
        <button :disabled="loading || !hasResults" @click="download">JSON herunterladen</button>
      </div>
      <p class="milon-dev__secondary">Der Download enthält Testprofil und Trainingsdaten. API-Zugangsdaten, Tokens und signierte URLs werden entfernt.</p>
    </section>

    <section class="milon-dev__card" aria-labelledby="milon-results" :aria-busy="loading ? 'true' : 'false'">
      <h2 id="milon-results">Abrufe <span class="milon-dev__count">{{ finished }} / {{ rows.length }}</span></h2>
      <p aria-live="polite">{{ progress }}</p>
      <article v-for="row in rows" :key="row.kind" class="milon-dev__result">
        <div class="milon-dev__result-head"><h3>{{ row.title }}</h3><span class="milon-dev__state" :class="'milon-dev__state--' + row.state">{{ stateLabel(row.state) }}</span></div>
        <p class="milon-dev__endpoint">{{ row.endpoint }}</p>
        <p v-if="row.error" class="milon-dev__error">{{ row.error }}</p>
        <details v-if="row.result"><summary>JSON-Antwort ansehen</summary><pre>{{ preview(row.result) }}</pre></details>
      </article>
    </section>
  </main>
</template>

<script>
import api from "@/api/milonApi"
import { saveAs } from "file-saver"

const sections = [
  ["connection", "Verbindung & Studio", "GET /whoami"],
  ["user", "Testprofil", "GET /v1/user"],
  ["userList", "Benutzersuche", "GET /v1/user/list · Test User"],
  ["trainingByUser", "Trainingsdaten · nur Benutzer-ID", "GET /v1/training?careStudioUserId=9153"],
  ["strength", "Krafttraining", "GET /v1/training · strength"],
  ["cardio", "Cardiotraining", "GET /v1/training · cardio"],
  ["ages", "Muscle Age, Mobility Age & Bio Age", "GET /v1/metrics/aggregated"],
  ["sarcopenia", "Sarkopenie & Kraftreferenzen", "GET /v1/metrics/sarcopenia"],
  ["devices", "Gerätemetadaten", "GET /v1/training/device-metadata"],
]
const makeRows = () => sections.map(([kind, title, endpoint]) => ({ kind, title, endpoint, state: "idle", result: null, error: "" }))

export default {
  name: "MilonStats",
  data() {
    const now = Date.now()
    return { from: new Date(now - 29 * 86400000).toISOString().slice(0, 10), to: new Date(now).toISOString().slice(0, 10), raw: true, loading: false, rows: makeRows(), startedAt: null, exportOptions: null, disposed: false }
  },
  computed: {
    rangeError() {
      if (!this.from || !this.to) return "Bitte einen Zeitraum angeben."
      const days = (Date.parse(this.to) - Date.parse(this.from)) / 86400000 + 1
      return !Number.isFinite(days) || days < 1 || days > 31 ? "Bitte einen Zeitraum von 1 bis 31 Tagen wählen." : ""
    },
    finished() { return this.rows.filter((row) => ["done", "partial", "error", "skipped"].includes(row.state)).length },
    hasResults() { return this.rows.some((row) => row.result) },
    progress() {
      if (this.loading) return "Die Bereiche werden nacheinander geladen. Bitte diese Seite geöffnet lassen."
      if (!this.startedAt) return "Bereit zum Laden."
      if (this.rows.every((row) => row.state === "done")) return "Alle Bereiche wurden geladen. Auch leere Antworten sind für die Analyse hilfreich."
      return "Der Abruf ist beendet. Bitte auch bei Fehlern oder unvollständigen Daten das JSON herunterladen."
    },
  },
  beforeDestroy() { this.disposed = true },
  methods: {
    stateLabel(state) { return { idle: "Bereit", loading: "Lädt …", done: "Geladen", partial: "Unvollständig", error: "Fehler", skipped: "Übersprungen" }[state] },
    preview(result) {
      const text = JSON.stringify(result, null, 2)
      return text.length > 24000 ? text.slice(0, 24000) + "\n… Vorschau gekürzt. Der Download enthält die vollständige geladene Antwort." : text
    },
    async loadData() {
      if (this.loading || this.rangeError) return
      this.rows = makeRows()
      this.loading = true
      this.startedAt = new Date().toISOString()
      this.exportOptions = { from: this.from, to: this.to, raw: this.raw }
      try {
        for (const row of this.rows) {
          if (this.disposed) break
          row.state = "loading"
          try {
            row.result = await api.fetchMilonDevData({ ...this.exportOptions, kind: row.kind })
            row.state = row.result.ok ? (row.result.complete ? "done" : "partial") : "error"
            row.error = row.result.error || ""
          } catch (error) {
            const status = error.response && error.response.status
            const body = error.response && error.response.data
            row.error = status === 401 || status === 403 ? "Bitte mit einem Konto mit Statistik-Adminrecht anmelden." : (body && typeof body.error === "string" ? body.error : "Abruf fehlgeschlagen. Verbindung oder Backend prüfen.")
            row.result = { ok: false, complete: false, status: status || null, error: row.error }
            row.state = "error"
          }
          if (row.kind === "connection" && row.state !== "done") {
            this.rows.filter((item) => item.state === "idle").forEach((item) => { item.state = "skipped" })
            break
          }
        }
      } finally { this.loading = false }
    },
    download() {
      if (this.loading || !this.hasResults) return
      const data = { format: "roody-milon-dev-v1", startedAt: this.startedAt, exportedAt: new Date().toISOString(), target: { env: "staging", userId: 9153, studioId: 1040, studioGroupId: 1281 }, options: this.exportOptions, complete: this.rows.every((row) => row.state === "done"), results: this.rows.map(({ kind, state, result }) => ({ kind, state, result })) }
      saveAs(new Blob([JSON.stringify(data, null, 2)], { type: "application/json;charset=utf-8" }), "milon-test-9153-" + this.startedAt.replace(/[:.]/g, "-") + ".json")
    },
  },
}
</script>

<style scoped>
.milon-dev { max-width: 64rem; margin: 0 auto; padding: 1.5rem 1rem 4rem; font-size: 1rem; line-height: 1.6; color: #18352d; }
.milon-dev__header { margin-bottom: 1.5rem; }
.milon-dev h1 { font-size: 2rem; line-height: 1.2; margin: .75rem 0; }
.milon-dev h2 { font-size: 1.25rem; margin: 0 0 .75rem; }
.milon-dev h3 { font-size: 1rem; margin: 0; }
.milon-dev p { margin: .5rem 0 1rem; }
.milon-dev__badge, .milon-dev__state { display: inline-block; padding: .25rem .65rem; border-radius: .5rem; background: #e8f1ed; font-size: .75rem; font-weight: 700; }
.milon-dev__card { padding: 1.5rem; background: #fff; border: 1px solid #dbe6e0; border-radius: 1rem; margin-bottom: 1.25rem; }
.milon-dev__secondary, .milon-dev__endpoint { font-size: .875rem; color: #51685e; }
.milon-dev__fields, .milon-dev__actions { display: flex; flex-wrap: wrap; gap: 1rem; margin: 1rem 0; }
.milon-dev__fields label { display: flex; flex-direction: column; gap: .35rem; flex: 1 1 13rem; font-size: .875rem; }
.milon-dev input[type="date"] { width: 100%; min-width: 0; padding: .7rem; border: 1px solid #839d90; border-radius: .5rem; font-size: 1rem; background: white; color: #18352d; }
.milon-dev__check { display: flex; align-items: center; gap: .75rem; font-size: .875rem; }
.milon-dev__check input { width: 1.25rem; height: 1.25rem; flex-shrink: 0; accent-color: #24684e; }
.milon-dev button { padding: .7rem 1rem; border: 1px solid #24684e; border-radius: .6rem; font-size: 1rem; font-weight: 600; color: #205c45; }
.milon-dev button.milon-dev__primary { background: #24684e; color: white; }
.milon-dev button:disabled { opacity: .55; cursor: not-allowed; }
.milon-dev :focus-visible { outline: 3px solid #478fbd; outline-offset: 3px; }
.milon-dev__result { border-top: 1px solid #e1e9e4; padding: 1rem 0; }
.milon-dev__result-head { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: .5rem; }
.milon-dev__count { font-size: .875rem; color: #51685e; margin-left: .5rem; }
.milon-dev__error { color: #9d3030; font-size: .875rem; overflow-wrap: anywhere; }
.milon-dev__state--error, .milon-dev__state--partial { background: #fff0df; color: #7d420c; }
.milon-dev__endpoint { overflow-wrap: anywhere; }
.milon-dev summary { cursor: pointer; font-size: .875rem; color: #205c45; padding: .4rem 0; }
.milon-dev pre { max-height: 26rem; overflow: auto; padding: 1rem; border-radius: .5rem; background: #f2f6f3; font-size: .75rem; line-height: 1.6; white-space: pre-wrap; overflow-wrap: anywhere; }
@media (max-width: 36rem) { .milon-dev__card { padding: 1rem; } .milon-dev__actions button { width: 100%; } }
</style>
